import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiResponse, CallTariffProvenance, CallNetDiagnostics } from '@klaro7/shared-types';

import api from '@/lib/api';

export interface DailyRevenue {
  date: string;
  deposits: {
    count: number;
    totalAmount: number;
    /** Deposits total of the previous day, for the day-over-day delta. */
    prevTotalAmount: number;
    items: Array<{
      id: string;
      at: string;
      amount: number;
      user: { id: string; displayName: string; email: string };
    }>;
  };
  newUsers: {
    /** Regular users registered in the day. */
    count: number;
    /** Same count for the previous day, for the day-over-day delta. */
    prevCount: number;
  };
  calls: {
    connected: number;
    completed: number;
    /** Connected calls of the previous day, for the day-over-day delta. */
    prevConnected: number;
    totalConsultant: number;
    totalPlatform: number;
    items: Array<{
      id: string;
      at: string;
      callType: string;
      status: string;
      answered: boolean;
      /** Prova col consulente di test: in lista, ma fuori da contatori e totali. */
      isTest: boolean;
      durationSeconds: number;
      caller: { displayName: string };
      professional: { displayName: string };
      customerPaid: number | null;
      consultantEarning: number | null;
      platformEarning: number | null;
      /** Conversion bonus (7€) tied to THIS call, if it was the last-touch call before a recharge. */
      conversionEarning: number | null;
      /** Provenance of the applied tariff (bonus / promo / standard), possibly composed. */
      tariff: CallTariffProvenance;
    }>;
  };
}

async function fetchDailyRevenue(date: string): Promise<DailyRevenue> {
  const res = await api.get<ApiResponse<DailyRevenue>>('/revenue/daily', { params: { date } });
  return res.data.data;
}

export function useDailyRevenue(date: string) {
  return useQuery({
    queryKey: ['revenue', 'daily', date],
    queryFn: () => fetchDailyRevenue(date),
  });
}

export interface OnlineUsers {
  /** clients + professionals; admins are excluded. */
  total: number;
  clients: number;
  professionals: number;
}

async function fetchOnlineUsers(): Promise<OnlineUsers> {
  const res = await api.get<ApiResponse<OnlineUsers>>('/revenue/online-users');
  return res.data.data;
}

/** Live "who's on the platform right now" — polls every 30s while mounted. */
export function useOnlineUsers() {
  return useQuery({
    queryKey: ['revenue', 'online-users'],
    queryFn: fetchOnlineUsers,
    refetchInterval: 30_000,
  });
}

export interface CallDetail {
  id: string;
  callType: string;
  status: string;
  answered: boolean;
  initiatedAt: string;
  answeredAt: string | null;
  connectedAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  endReason: string | null;
  callerUserAgent: string | null;
  callerDeviceModel: string | null;
  professionalUserAgent: string | null;
  professionalDeviceModel: string | null;
  /** Diagnostica tecnica della connessione per lato; null se non rilevata. */
  callerNetDiag: CallNetDiagnostics | null;
  professionalNetDiag: CallNetDiagnostics | null;
  caller: { id: string; displayName: string };
  professional: { id: string; displayName: string };
  customerPaid: number | null;
  consultantEarning: number | null;
  platformEarning: number | null;
  /** Conversion bonus (7€) tied to THIS call, if it was the last-touch call before a recharge. */
  conversionEarning: number | null;
  /** Provenance of the applied tariff (bonus / promo / standard), possibly composed. */
  tariff: CallTariffProvenance;
  transcription: string | null;
  /** Diarized transcription (who said what); null for legacy calls with plain text only. */
  transcriptionSegments: Array<{ speaker: 'caller' | 'professional' | 'unknown'; text: string }> | null;
  transcriptionStatus: 'none' | 'processing' | 'completed' | 'failed' | 'skipped';
  /** True when the recording file is still available, so the transcription can be regenerated. */
  hasRecording: boolean;
  /** Chiamata di prova col consulente di test: l'unica di cui si possono cancellare audio e trascrizione. */
  isTest: boolean;
  messages: Array<{ sender: 'caller' | 'professional'; content: string; at: string }> | null;
}

async function fetchCallDetail(id: string): Promise<CallDetail> {
  const res = await api.get<ApiResponse<CallDetail>>(`/revenue/calls/${id}`);
  return res.data.data;
}

export function useCallDetail(id: string) {
  return useQuery({
    queryKey: ['revenue', 'call', id],
    queryFn: () => fetchCallDetail(id),
    enabled: Boolean(id),
    // Poll while a (re)transcription is running so the result appears on its own.
    refetchInterval: (query) => (query.state.data?.transcriptionStatus === 'processing' ? 3000 : false),
  });
}

export function useRetranscribeCall(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<ApiResponse<{ transcriptionStatus: string }>>(`/revenue/calls/${id}/retranscribe`);
      return res.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['revenue', 'call', id] });
    },
  });
}

/** Cancella audio e trascrizione di una chiamata di PROVA; il server rifiuta (403) qualunque chiamata vera. */
export function useDeleteTestCallRecording(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/revenue/calls/${id}/recording`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['revenue', 'call', id] });
    },
  });
}

/** Recording blob for the admin player; Bearer auth travels normally (an <audio src> could not send it). */
export async function fetchCallRecordingBlob(id: string): Promise<Blob> {
  const res = await api.get(`/revenue/calls/${id}/recording`, { responseType: 'blob' });
  return res.data as Blob;
}

/* -------------------------------------------------------------------------- */
/*  Report amministrativi (Marletta, 03/08/2026)                              */
/* -------------------------------------------------------------------------- */

export interface CustomerDepositsReport {
  /** Mesi affiancati, in ordine cronologico (YYYY-MM). */
  months: string[];
  rows: Array<{
    userId: string;
    nome: string;
    email: string;
    /** Versato per mese, nello stesso ordine di `months`. */
    perMese: number[];
    totale: number;
  }>;
  totaliPerMese: number[];
  totaleGenerale: number;
}

export interface BonusCostsReport {
  from: string;
  to: string;
  rows: Array<{
    source: string;
    label: string;
    blocchi: number;
    erogato: number;
    /** Quota davvero spesa in chiamate: il costo effettivo. */
    consumato: number;
    /** Quota stornata perché scaduta: non è mai uscita dalla piattaforma. */
    scaduto: number;
    residuoAttivo: number;
  }>;
  totali: { blocchi: number; erogato: number; consumato: number; scaduto: number; residuoAttivo: number };
}

export function useCustomerDeposits(from: string, to: string) {
  return useQuery({
    queryKey: ['revenue', 'reports', 'customer-deposits', from, to],
    queryFn: async () => {
      const res = await api.get<ApiResponse<CustomerDepositsReport>>(
        '/revenue/reports/customer-deposits',
        { params: { from, to } }
      );
      return res.data.data;
    },
  });
}

/** from/to: YYYY-MM (mesi) o YYYY-MM-DD (giorni), estremi inclusi. */
export function useBonusCosts(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['revenue', 'reports', 'bonus-costs', from, to],
    enabled,
    queryFn: async () => {
      const res = await api.get<ApiResponse<BonusCostsReport>>('/revenue/reports/bonus-costs', {
        params: { from, to },
      });
      return res.data.data;
    },
  });
}
